import React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DialpadIcon from '@mui/icons-material/Dialpad';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import Container from '@mui/material/Container';
import { Paper, Autocomplete, Divider, Stack } from '@mui/material';
import { StateManager } from '../utilities/stateManager';
import firebase from '../utilities/firebase';
import RequestManager from '../utilities/requestManager';
import history from '../utilities/history';
import AdminToast from '../components/AdminToast';
import {
  establishSession, getRememberedPinUser, forgetRememberedPinUser, takePostLoginRedirect,
} from '../utilities/session';

// Finish login by landing on wherever the user was originally headed — a load link, a
// car, a service order — falling back to a plain reload of the current page. Both the
// email and PIN paths go through this so they can't diverge.
//
// A full page load (not history.push) because the app reads auth and permissions from
// storage during startup; a client-side transition would render the target before the
// permission state it depends on exists.
const finishLogin = () => {
  const target = takePostLoginRedirect();
  setTimeout(() => {
    if (target) window.location.href = target;
    else history.go(0);
  }, 500);
};

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://skywayclassics.com/">
        Skyway Classics
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 30,
    textAlign: "left",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  keypadButton: {
    height: 64,
    fontSize: '1.5rem',
    fontWeight: 500,
  },
}));

// The PIN roster (names of PIN-enabled staff) is cached so the search filters
// instantly and still works if the shop wifi drops mid-shift.
const ROSTER_CACHE_KEY = 'pinRosterCache';
const MIN_PIN_LENGTH = 4;
const MAX_PIN_LENGTH = 8;

// The cache exists so the shop tablet can still show names on a flaky connection, but
// it must never be why a newly created user can't be found. Anything older than this is
// ignored, and a successful fetch always replaces it.
const ROSTER_CACHE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const readCachedRoster = () => {
  try {
    const raw = localStorage.getItem(ROSTER_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed?.users)) return [];
    if (!parsed.at || Date.now() - parsed.at > ROSTER_CACHE_MAX_AGE_MS) return [];
    return parsed.users;
  } catch (e) {
    return [];
  }
};

export default function SignIn() {
  const classes = useStyles();

  const [form, setForm] = React.useState({});
  const [remember, setRemeber] = React.useState(true);
  const [showAdminToast, setShowAdminToast] = React.useState(false);
  const [mode, setMode] = React.useState('email');

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAdminParam = urlParams.get('isAdmin');
    const today = new Date().toISOString().slice(0, 10);
    if (isAdminParam === today) {
      setShowAdminToast(true);
    }
  }, []);

  const onChange = (e) => {
    const {value, name, id} = e.target;
    let newForm = {...form, [id || name] : value};
    setForm(newForm);
  }

  const submitForm = async () => {
    const { email, password } = form;
    if(!email || !password ) return StateManager.setAlertAndOpen("Please complete form", "error");

    StateManager.setLoading(true);
    try{
      const userRecord = await firebase.auth().signInWithEmailAndPassword(email, password);
      let user = await firebase.firestore().doc(`users/${userRecord.user.uid}`).get();
      user = user.data();

      establishSession({ uid: userRecord.user.uid, user, remember });

      StateManager.setAlertAndOpen("Login Successful!", "success");
      finishLogin();
    }
    catch(e) {
      console.log(e)
      StateManager.setAlertAndOpen(e.message, "error");
    }

    StateManager.setLoading(false);
  }

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      {showAdminToast ? (
        <AdminToast />
      ) : mode === 'pin' ? (
        <PinSignIn classes={classes} onUseEmail={() => setMode('email')} />
      ) : (
        <Paper className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <form className={classes.form} noValidate>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              onChange={onChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              onChange={onChange}
            />
            <FormControlLabel
              control={<Checkbox defaultChecked value={remember} onClick={() => setRemeber(!remember)} color="primary" />}
              label="Remember me"
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={submitForm}
            >
              Sign In
            </Button>
            <Grid container>
              <Grid item xs>
                <Link href="#" variant="body2">
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link href="/new-user" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<DialpadIcon />}
              onClick={() => setMode('pin')}
            >
              Sign in with a PIN
            </Button>
          </form>
        </Paper>
      )}
      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
}

// PIN sign-in: find yourself by name, then enter your PIN. Ends in
// signInWithCustomToken against the same uid an email/password login would use, so
// the resulting session is indistinguishable downstream.
function PinSignIn({ classes, onUseEmail }) {
  // State, not a memo: "Switch user" has to be able to forget it. As a memo it kept
  // its mount-time value, so clearing the name just re-applied it.
  const [remembered, setRemembered] = React.useState(() => getRememberedPinUser());

  const [roster, setRoster] = React.useState(() => readCachedRoster());
  const [selected, setSelected] = React.useState(null);
  const [pin, setPin] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const pinInputRef = React.useRef(null);
  // The remembered name is applied at most once. Without this the effect below
  // re-selected it the instant you cleared the field, so the autocomplete's "x" and
  // "Switch user" both looked broken.
  const autoSelected = React.useRef(false);

  // Always refetch the roster; the cached copy is only what's on screen until this
  // lands. Exposed as refreshRoster so "not finding" someone is recoverable in the UI
  // rather than requiring a hard reload — a user created a minute ago must be findable.
  const [rosterLoading, setRosterLoading] = React.useState(true);

  const refreshRoster = React.useCallback(async () => {
    setRosterLoading(true);
    const res = await RequestManager.get({ function: 'pinLoginRoster' });
    setRosterLoading(false);
    if (!res?.success || !Array.isArray(res.users)) return false;
    setRoster(res.users);
    try {
      localStorage.setItem(ROSTER_CACHE_KEY, JSON.stringify({ at: Date.now(), users: res.users }));
    } catch (e) {
      // Cache is a nicety; a full quota shouldn't break login.
    }
    return true;
  }, []);

  React.useEffect(() => { refreshRoster(); }, [refreshRoster]);

  // Pre-select the last person who signed in on this device so they only type digits.
  React.useEffect(() => {
    if (autoSelected.current || !remembered || selected) return;
    const match = roster.find(u => u.uid === remembered.uid);
    if (match) {
      autoSelected.current = true;
      setSelected(match);
    }
  }, [roster, remembered, selected]);

  const chooseSomeoneElse = () => {
    forgetRememberedPinUser();
    autoSelected.current = true; // don't re-apply the old name
    setRemembered(null);
    setSelected(null);
    setPin('');
  };

  // Move focus to the PIN box as soon as a name is chosen, so typing goes to the PIN
  // instead of the name search.
  React.useEffect(() => {
    if (selected && pinInputRef.current) pinInputRef.current.focus();
  }, [selected]);

  const label = (u) => (u ? `${u.firstName} ${u.lastName}`.trim() : '');

  const submit = React.useCallback(async () => {
    if (!selected) return StateManager.setAlertAndOpen('Search for your name first', 'error');
    if (pin.length < MIN_PIN_LENGTH) return StateManager.setAlertAndOpen('Enter your PIN', 'error');

    setBusy(true);
    StateManager.setLoading(true);
    try {
      const res = await RequestManager.post({
        function: 'pinLogin',
        variables: { uid: selected.uid, pin },
      });

      if (!res?.success) {
        setPin('');
        throw new Error(res?.error || 'Could not sign in');
      }

      await firebase.auth().signInWithCustomToken(res.token);
      establishSession({ uid: selected.uid, user: res.user, remember: true, viaPin: true });

      StateManager.setAlertAndOpen(`Welcome, ${res.user.firstName || 'there'}!`, 'success');
      finishLogin();
    } catch (e) {
      StateManager.setAlertAndOpen(e.message, 'error');
    }
    StateManager.setLoading(false);
    setBusy(false);
  }, [selected, pin]);

  const press = (digit) => setPin(prev => (
    prev.length >= MAX_PIN_LENGTH ? prev : prev + digit
  ));

  // No window-level key listener: the PIN field above is a real input, so typing and
  // Enter are handled natively. A global listener on top of it double-appended digits.
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <Paper className={classes.paper}>
      <Avatar className={classes.avatar}>
        <DialpadIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        PIN Sign In
      </Typography>

      <Box className={classes.form}>
        <Autocomplete
          options={roster}
          value={selected}
          onChange={(e, value) => {
            // Clearing via the "x" must stick — mark the remembered name as spent so
            // the pre-select effect doesn't put it straight back.
            if (!value) autoSelected.current = true;
            setSelected(value);
            setPin('');
          }}
          getOptionLabel={label}
          isOptionEqualToValue={(option, value) => option.uid === value.uid}
          autoHighlight
          openOnFocus={false}
          // Nothing renders until they type, and never more than a handful of names.
          filterOptions={(options, state) => {
            const query = state.inputValue.trim().toLowerCase();
            if (!query) return [];
            return options
              .filter(u => (
                u.firstName.toLowerCase().includes(query) ||
                u.lastName.toLowerCase().includes(query)
              ))
              .slice(0, 8);
          }}
          loading={rosterLoading}
          noOptionsText={
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {rosterLoading ? 'Loading names…' : 'No match found.'}
              </Typography>
              {!rosterLoading && (
                <Button
                  size="small"
                  variant="outlined"
                  onMouseDown={e => e.preventDefault()}
                  onClick={async () => {
                    const ok = await refreshRoster();
                    StateManager.setAlertAndOpen(
                      ok ? 'Name list refreshed' : 'Could not reach the server',
                      ok ? 'success' : 'error',
                    );
                  }}
                >
                  Refresh name list
                </Button>
              )}
            </Box>
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              margin="normal"
              label="Start typing your name"
              autoFocus={!selected}
            />
          )}
        />

        {selected && (
          <Typography variant="body2" color="textSecondary">
            Not you?{' '}
            <Link component="button" type="button" variant="body2" onClick={chooseSomeoneElse}>
              Switch user
            </Link>
          </Typography>
        )}

        {/* A real input, not decorative dots: it can be typed into directly (keyboard
            or phone numeric pad) and shows exactly the digits entered. The old dots
            always drew four placeholders, which read as "enter 4 digits" even though
            issued PINs are six. */}
        <TextField
          fullWidth
          value={pin}
          inputRef={pinInputRef}
          disabled={!selected || busy}
          placeholder="Enter your PIN"
          onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, MAX_PIN_LENGTH))}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
            style: { textAlign: 'center', fontSize: '2rem', letterSpacing: '0.4rem', fontWeight: 600 },
          }}
          sx={{ my: 2 }}
        />

        <Grid container spacing={1}>
          {keys.map(key => (
            <Grid item xs={4} key={key}>
              <Button
                fullWidth
                variant="outlined"
                className={classes.keypadButton}
                disabled={!selected || busy}
                onClick={() => press(key)}
              >
                {key}
              </Button>
            </Grid>
          ))}
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              className={classes.keypadButton}
              disabled={!selected || busy || !pin}
              onClick={() => setPin('')}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              className={classes.keypadButton}
              disabled={!selected || busy}
              onClick={() => press('0')}
            >
              0
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              className={classes.keypadButton}
              disabled={!selected || busy || !pin}
              onClick={() => setPin(prev => prev.slice(0, -1))}
            >
              <BackspaceOutlinedIcon />
            </Button>
          </Grid>
        </Grid>

        <Stack spacing={1} sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!selected || busy || pin.length < MIN_PIN_LENGTH}
            onClick={submit}
          >
            Sign In
          </Button>
          <Button fullWidth variant="text" color="inherit" onClick={onUseEmail}>
            Sign in with email instead
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
